import { StatusCodes } from "http-status-codes";
import { TaJson } from "ta-json";

import { NullableResultPromise } from "../base-types";
import { IPolicy } from "../contracts/policies/policy";
import { IUserGroupPolicy } from "../contracts/policies/user-group-policy";
import { IUserPolicy } from "../contracts/policies/user-policy";
import ErrorMessages from "../error-messages";
import { InvalidOperationError } from "../errors/invalid-operation-error";
import Guard from "../guard";
import { PolicyMapper } from "../mappers/policy-mapper";
import { PolicyResource } from "../models/policies/policy-resource";
import { IExtendedContentHubClient } from "./extended-client";
import { ResponseHandler } from "./response-handler";

/**
 * Contains functionality to get and update policies.
 */
export interface IPoliciesClient {
    /**
     * Gets the policy from specified user.
     *
     * @remarks
     * The specified user group id must be a strictly positive number.
     *
     * @param userGroupId - Id of the user group to get the policy from
     * @returns The policy or null when it was not found.
     */
    getUserGroupPolicyAsync(userGroupId: number): NullableResultPromise<IUserGroupPolicy>;

    /**
     * Gets the policy from specified user.
     *
     * @remarks
     * The specified user id must be a strictly positive number.
     *
     * @param userId - Id of the user to get the policy from
     * @returns The policy or null when it was not found.
     */
    getUserPolicyAsync(userId: number): NullableResultPromise<IUserPolicy>;

    /**
     * Updates the specified policy.
     *
     * @remarks
     * Throws an error when the policy could not be saved because a validation error occurred.
     *
     * @param policy - The policy to update
     */
    updateAsync(policy: IPolicy): Promise<void>;
}

export class PoliciesClient implements IPoliciesClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNull(client);
        this._client = client;
    }

    async getUserGroupPolicyAsync(userGroupId: number): NullableResultPromise<IUserGroupPolicy> {
        Guard.validId(userGroupId);

        const resource = await this.getPolicyResourceAsync(userGroupId);
        const result = await PolicyMapper.mapToUserGroupPolicyAsync(this._client, resource);

        return result;
    }

    async getUserPolicyAsync(userId: number): NullableResultPromise<IUserPolicy> {
        Guard.validId(userId);

        const resource = await this.getPolicyResourceAsync(userId);
        const result = await PolicyMapper.mapToUserPolicyAsync(this._client, resource);

        return result;
    }

    async updateAsync(policy: IPolicy): Promise<void> {
        Guard.notNull(policy);

        const resource = await PolicyMapper.mapToResourceAsync(this._client, policy);

        const id = policy.getRelatedId();
        if (id == null) {
            throw new InvalidOperationError(ErrorMessages.PoliciesClient.RelatedIdCannotBeNull);
        }

        const link = await this._client.linkHelper.policyToLinkAsync(id);
        const content = TaJson.serialize(resource) as unknown;
        const response = await this._client.raw.postAsync(link.href, content);

        ResponseHandler.handleErrors(response);
    }

    //#region Private methods

    private async getPolicyResourceAsync(id: number): NullableResultPromise<PolicyResource> {
        const link = await this._client.linkHelper.policyToLinkAsync(id);

        const response = await this._client.raw.getAsync(link.href);

        if (response.statusCode === StatusCodes.NOT_FOUND) {
            return null;
        }

        ResponseHandler.handleErrors(response);

        const resource = TaJson.deserialize<PolicyResource>(response.content, PolicyResource);

        return resource;
    }

    //#endregion
}
